import React, { useState, useMemo } from 'react';
import { StandardSection, createEntitySectionConfig } from '../common';
import type { TableColumn } from '../common';
import type { ServicioTipo, Empresa } from '../../types';
import { servicioTipoFormFields } from '../../config/formFields';

interface ServiciosSectionProps {
  data: {
    serviciosTipo: ServicioTipo[];
    empresas: Empresa[];
  };
  operations: {
    handleServicioTipoSubmit: (data: Record<string, any>) => Promise<void>;
    handleServicioTipoDelete: (item: ServicioTipo) => Promise<void>;
    handleServicioTipoEdit: (item: ServicioTipo, key: string, newValue: any) => Promise<void>;
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

export const ServiciosSection: React.FC<ServiciosSectionProps> = ({ data, operations, userPermissions }) => {
  const [empresaFilter, setEmpresaFilter] = useState<string>(''); // Empty string = show all
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: string } | null>({ key: 'item', direction: 'ascending' });

  const sortedServicios = useMemo(() => {
    // First filter by empresa if a filter is selected
    let sortableItems = empresaFilter
      ? data.serviciosTipo.filter(s => s.empresaCodigo === empresaFilter)
      : [...data.serviciosTipo];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const aVal = (a as any)[sortConfig.key];
        const bVal = (b as any)[sortConfig.key];
        
        // Handle null or undefined values
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        if (typeof aVal === 'string' && typeof bVal === 'string') {
            return aVal.localeCompare(bVal, undefined, { numeric: true }) * (sortConfig.direction === 'ascending' ? 1 : -1);
        } else {
            if (aVal < bVal) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aVal > bVal) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
        }
        return 0;
      });
    }
    return sortableItems;
  }, [data.serviciosTipo, sortConfig, empresaFilter]);

  const handleSort = (key: string) => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Populate empresa options dynamically
  const empresaOptions = data.empresas.map(emp => ({
    value: emp.codigo,
    label: emp.nombre
  }));

  // Table column configurations
  const servicioTipoColumns: TableColumn<ServicioTipo>[] = [
    {
      key: 'empresaCodigo',
      title: 'Empresa',
      width: '12%',
      editable: true,
      sortable: true,
      editType: 'select',
      selectOptions: [
        { value: '', label: '-- Sin asignar --' },
        ...empresaOptions
      ],
      onEdit: operations.handleServicioTipoEdit,
      render: (value) => {
        const empresa = data.empresas.find(e => e.codigo === value);
        return empresa ? empresa.nombre : <span style={{ color: '#999', fontStyle: 'italic' }}>Sin empresa</span>;
      }
    },
    {
      key: 'item',
      title: 'COD. OP.',
      width: '7%',
      editable: true,
      sortable: true,
      editType: 'text',
      onEdit: operations.handleServicioTipoEdit,
      render: (value) => value ? `${value}` : '-'
    },
    {
      key: 'codigoMarbar',
      title: 'Código MARBAR',
      width: '13%',
      editable: true,
      sortable: true,
      editType: 'text',
      onEdit: operations.handleServicioTipoEdit,
      render: (value) => value ? `${value}` : 'Sin código'
    },
    {
      key: 'descripcion',
      title: 'Descripción',
      width: '22%',
      editable: true,
      sortable: true,
      editType: 'text',
      onEdit: operations.handleServicioTipoEdit
    },
    {
      key: 'costoUnitario',
      title: 'Costo Unitario',
      width: '15%',
      editable: true,
      sortable: true,
      editType: 'number',
      onEdit: operations.handleServicioTipoEdit,
      render: (value) => value ? `$${value}` : '$0'
    },
    {
      key: 'unidadCobro',
      title: 'Unidad Cobro',
      width: '15%',
      editable: true,
      sortable: true,
      editType: 'select',
      selectOptions: [
        { value: 'día', label: 'Día' },
        { value: 'serv', label: 'Servicio' },
        { value: 'hr', label: 'Hora' },
        { value: 'uu', label: 'uu' },
        { value: 'km', label: 'km' },
        { value: 'kg', label: 'kg' },
        { value: 'l', label: 'l' },
        { value: 'pieza', label: 'pieza' }
      ],
      onEdit: operations.handleServicioTipoEdit
    },
    {
      key: 'activo',
      title: 'Estado',
      width: '10%',
      render: (value) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 'bold',
          backgroundColor: value ? '#e8f5e8' : '#ffebee',
          color: value ? '#2e7d32' : '#c62828'
        }}>
          {value ? 'Activo' : 'Inactivo'}
        </span>
      )
    }
  ];

  // Quick Setup Handler for standard services
  const handleServiciosQuickSetup = async () => {
    if (window.confirm('¿Cargar catálogo completo de servicios? Esta acción creará 70 servicios del análisis Excel LP-Servicios con códigos MARBAR auténticos y costos reales.')) {
      // Complete Excel service catalog from LP-Servicios sheet - ACTUAL DATA FROM EXCEL
      const servicioTemplates = [
        { descripcion: 'PERSONAL C.SOLID-Tecnico x Persona2Rig', costoUnitario: 510398.5, unidadCobro: 'día', codigoMarbar: '006001000000001', item: '1', activo: true },
        { descripcion: 'PERSONAL C.SOLID-Stnd By Tecnico 2Rig', costoUnitario: 459358.65, unidadCobro: 'día', codigoMarbar: '006001000000022', item: '2', activo: true },
        { descripcion: 'PERSONAL C.SOLID-Transport NQN-Yac 2Rig', costoUnitario: 4441.05, unidadCobro: 'km', codigoMarbar: '006001000000013', item: '3', activo: true },
        { descripcion: 'CONT SOLIDO-Centrifuga Op x equip 2Rig', costoUnitario: 261542.26, unidadCobro: 'día', codigoMarbar: '006001000000059', item: '4', activo: true },
        { descripcion: 'CONT SOLIDO-Centrif Std ByXequip 2Rig', costoUnitario: 183079.58, unidadCobro: 'día', codigoMarbar: '006001000000067', item: '5', activo: true },
        { descripcion: 'CONT SOLIDO-Trailer Laboratorio 2Rig', costoUnitario: 84761.66, unidadCobro: 'día', codigoMarbar: '006001000000520', item: '6', activo: true },
        { descripcion: 'CONT SOLIDO-Trailer viviend s/ Lab 2Rig', costoUnitario: 48628.74, unidadCobro: 'día', codigoMarbar: '006001000000496', item: '7', activo: true },
        { descripcion: 'CIRCUITO (2 Rigs)-Pileta c/Remov(DiaXU)', costoUnitario: 161618.09, unidadCobro: 'día', codigoMarbar: '006001000000510', item: '8', activo: true },
        { descripcion: 'CIRCUITO (2 Rigs)-Pileta preparadora', costoUnitario: 274884.68, unidadCobro: 'día', codigoMarbar: '006001000000504', item: '9', activo: true },
        { descripcion: 'CIRCUITO (2 Rigs)-Tanque CaCl2 (DiaxU)', costoUnitario: 107041.66, unidadCobro: 'día', codigoMarbar: '006001000000508', item: '10', activo: true },
        { descripcion: 'CIRCUITO (2 Rigs)-Miscelaneos Conexión', costoUnitario: 916800.0, unidadCobro: 'día', codigoMarbar: '006001000000513', item: '11', activo: true },
        { descripcion: 'CIRCUITO (1 Rig)-Pileta c/Remov(DiaXU)', costoUnitario: 155714.5, unidadCobro: 'día', codigoMarbar: '006001000000111', item: '12', activo: true },
        { descripcion: 'CIRCUITO (1 Rig)-Pileta preparadora', costoUnitario: 286338.21, unidadCobro: 'día', codigoMarbar: '006001000000474', item: '13', activo: true },
        { descripcion: 'CIRCUITO (1 Rig)-Tanque CaCl2 (Dia x U)', costoUnitario: 111501.73, unidadCobro: 'día', codigoMarbar: '006001000000507', item: '14', activo: true },
        { descripcion: 'CIRCUITO (1 Rig)-Miscelaneos Conexión', costoUnitario: 955000.0, unidadCobro: 'día', codigoMarbar: '006001000000506', item: '15', activo: true },
        { descripcion: 'DTM C.SOLID-Mov/Desmov(Base-Yac) 2Rig', costoUnitario: 7297868.28, unidadCobro: 'serv', codigoMarbar: '006001000000517', item: '16', activo: true },
        { descripcion: 'DTM C.SOLID-DTM entre PAD hast30Km 2Rig', costoUnitario: 5089318.02, unidadCobro: 'serv', codigoMarbar: '006001000000518', item: '17', activo: true },
        { descripcion: 'DTM C.SOLID-Km Adicional Traslado 2Rig', costoUnitario: 20570.9, unidadCobro: 'km', codigoMarbar: '006001000000336', item: '18', activo: true },
        { descripcion: 'DTM CIRCUIT-Mov/DesmXPilet,Tk(Bas-Yac)2R', costoUnitario: 1884155.84, unidadCobro: 'serv', codigoMarbar: '006001000000531', item: '19', activo: true },
        { descripcion: 'DTM CIRCUIT-DTM Pileta entPAD 30Km 2Rig', costoUnitario: 1583526.46, unidadCobro: 'serv', codigoMarbar: '006001000000522', item: '20', activo: true },
        { descripcion: 'DTM CIRCUIT-Km Adic Traslado Pileta 2Rig', costoUnitario: 20570.9, unidadCobro: 'km', codigoMarbar: '006001000000516', item: '21', activo: true },
        { descripcion: 'DTM CIRCUIT-Mov/Des Miscelan Base-Yac2Ri', costoUnitario: 3122620.27, unidadCobro: 'serv', codigoMarbar: '006001000000514', item: '22', activo: true },
        { descripcion: 'DTM CIRCUIT-DTM Miscel entr PAD30Km 2Rig', costoUnitario: 2624376.58, unidadCobro: 'serv', codigoMarbar: '006001000000521', item: '23', activo: true },
        { descripcion: 'DTM CIRCUIT-Km Adicional miscelaneo 2Rig', costoUnitario: 20570.9, unidadCobro: 'km', codigoMarbar: '006001000000515', item: '24', activo: true },
        { descripcion: 'CONSUMIBLES-Mallas de Zaranda 2Rig', costoUnitario: 475659.91, unidadCobro: 'pieza', codigoMarbar: '006001000000110', item: '25', activo: true },
        { descripcion: 'CONSUMIBLES-Floculante MARFLOC 103 2Rig', costoUnitario: 13018.13, unidadCobro: 'kg', codigoMarbar: '001022000000001', item: '26', activo: true },
        { descripcion: 'CONSUMIBLES-Floculante MARFLOC 108 2Rig', costoUnitario: 14797.0, unidadCobro: 'kg', codigoMarbar: '001022000000003', item: '27', activo: true },
        { descripcion: 'CONSUMIBLES-Ácido clorhídrico 2Rig', costoUnitario: 3189.24, unidadCobro: 'l', codigoMarbar: 'no figura en tango', item: '28', activo: true },
        { descripcion: 'GENERADOR C.SOLID-Dia opérativo+GO 2Rig', costoUnitario: 1459600.51, unidadCobro: 'día', codigoMarbar: '006001000000374', item: '29', activo: true },
        { descripcion: 'GENERADOR C.SOLID-Stand By sin GO 2Rig', costoUnitario: 875760.31, unidadCobro: 'día', codigoMarbar: '006001000000187', item: '30', activo: true },
        { descripcion: 'GENERADOR C.SOLID-Mov/Desm Base-Yac 2Rig', costoUnitario: 1884155.84, unidadCobro: 'serv', codigoMarbar: '006001000000532', item: '31', activo: true },
        { descripcion: 'GENERADOR C.SOLID-DTM entr PAD,30Km 2Rig', costoUnitario: 1583526.46, unidadCobro: 'serv', codigoMarbar: '006001000000529', item: '32', activo: true },
        { descripcion: 'GENERADOR C.SOLID-Km Adicional 2Rig', costoUnitario: 20570.9, unidadCobro: 'km', codigoMarbar: '006001000000505', item: '33', activo: true },
        { descripcion: 'P. MOVIL-Serv Tecnico x Persona 2Rig', costoUnitario: 510398.5, unidadCobro: 'día', codigoMarbar: '006001000000533', item: '34', activo: true },
        { descripcion: 'P. MOVIL-Std By Tecnico x person 2Rig', costoUnitario: 459358.65, unidadCobro: 'día', codigoMarbar: '006001000000534', item: '35', activo: true },
        { descripcion: 'P. MOVIL-Transp persona-viaje NQN-Yac 2R', costoUnitario: 4441.05, unidadCobro: 'km', codigoMarbar: '006001000000535', item: '36', activo: true },
        { descripcion: 'P. MOVIL-Pileta c/Remov(activ 2Rigs)', costoUnitario: 193927.68, unidadCobro: 'día', codigoMarbar: '006001000000512', item: '37', activo: true },
        { descripcion: 'P. MOVIL-Pileta c/Remov(activ 1Rig)', costoUnitario: 202008.0, unidadCobro: 'día', codigoMarbar: '006001000000511', item: '38', activo: true },
        { descripcion: 'P. MOVIL-Mov/Desm X Pilet (Bas-Yac) 2Rig', costoUnitario: 1884155.84, unidadCobro: 'serv', codigoMarbar: '006001000000268', item: '39', activo: true },
        { descripcion: 'P. MOVIL-DTM Pileta entPAD hast30Km 2Rig', costoUnitario: 1583526.46, unidadCobro: 'serv', codigoMarbar: '006001000000530', item: '40', activo: true },
        { descripcion: 'P. MOVIL-Km Adicional x Piletas 2Rig', costoUnitario: 20570.9, unidadCobro: 'km', codigoMarbar: '006001000000524', item: '41', activo: true },
        { descripcion: 'P. MOVIL-Generador 450 kVA + GO 2Rig', costoUnitario: 1459600.51, unidadCobro: 'día', codigoMarbar: '006001000000525', item: '42', activo: true },
        { descripcion: 'P. MOVIL-Generador450 Kva+GO Std By 2Rig', costoUnitario: 875760.31, unidadCobro: 'día', codigoMarbar: '006001000000526', item: '43', activo: true },
        { descripcion: 'P. MOVIL-Mov/Des Generador Base-Yac 2Rig', costoUnitario: 1884155.84, unidadCobro: 'serv', codigoMarbar: '006001000000272', item: '44', activo: true },
        { descripcion: 'P. MOVIL-DTM Generad PAD hast30Km 2Rig', costoUnitario: 1583526.46, unidadCobro: 'serv', codigoMarbar: '006001000000550', item: '45', activo: true },
        { descripcion: 'P. MOVIL-Km Adicional Generador 2Rig', costoUnitario: 20570.9, unidadCobro: 'km', codigoMarbar: '006001000000527', item: '46', activo: true },
        { descripcion: 'P. MOVIL-Luminaria x unidad 2Rig', costoUnitario: 61128.59, unidadCobro: 'día', codigoMarbar: '006001000000523', item: '47', activo: true },
        { descripcion: 'P. MOVIL-Mov/Des Luminaria Base-Yac 2Rig', costoUnitario: 661877.8, unidadCobro: 'serv', codigoMarbar: '006001000000528', item: '48', activo: true },
        { descripcion: 'ADICIONAL-Luminaria x unidad 2Rig', costoUnitario: 61128.59, unidadCobro: 'día', codigoMarbar: '006001000000138', item: '49', activo: true },
        { descripcion: 'ADICIONAL-Mov/Desm Luminaria Bas-Yac 2Ri', costoUnitario: 661877.8, unidadCobro: 'serv', codigoMarbar: '006001000000341', item: '50', activo: true },
        { descripcion: 'ADICIONAL-DTM Lumin ent PAD hast30Km 2Ri', costoUnitario: 564589.93, unidadCobro: 'serv', codigoMarbar: '006001000000342', item: '51', activo: true },
        { descripcion: 'ADICIONAL-Bomba Trasvase op 2Rig', costoUnitario: 92253.05, unidadCobro: 'día', codigoMarbar: '006001000000028', item: '52', activo: true },
        { descripcion: 'ADICIONAL-Bomba trasvase Std BY 2Rig', costoUnitario: 64577.14, unidadCobro: 'día', codigoMarbar: '006001000000367', item: '53', activo: true },
        { descripcion: 'ADICIONAL-Compresor 2Rig', costoUnitario: 54943.15, unidadCobro: 'día', codigoMarbar: '006001000000093', item: '54', activo: true },
        { descripcion: 'ADICIONAL-Compresor Stand By 2Rig', costoUnitario: 49448.84, unidadCobro: 'día', codigoMarbar: '006001000000094', item: '55', activo: true },
        { descripcion: 'ADICIONAL-Mov/Desmov Compresor 2Rig', costoUnitario: 1884155.84, unidadCobro: 'serv', codigoMarbar: '006001000000315', item: '56', activo: true },
        { descripcion: 'ADICIONAL-DTM Compresor 2Rig', costoUnitario: 1583526.46, unidadCobro: 'serv', codigoMarbar: '006001000000316', item: '57', activo: true },
        { descripcion: 'ADICIONAL-Manifold 2Rig', costoUnitario: 173258.8, unidadCobro: 'día', codigoMarbar: '006001000000275', item: '58', activo: true },
        { descripcion: 'ADICIONAL-Mov/Desmov Manifold 2Rig', costoUnitario: 1040873.42, unidadCobro: 'serv', codigoMarbar: '006001000000326', item: '59', activo: true },
        { descripcion: 'ADICIONAL-DTM Manifold 2Rig', costoUnitario: 874792.2, unidadCobro: 'serv', codigoMarbar: '006001000000327', item: '60', activo: true },
        { descripcion: 'ADICIONAL-Manguerote4" c/ acople5Mt 2Rig', costoUnitario: 48716.23, unidadCobro: 'pieza', codigoMarbar: '006001000000328', item: '61', activo: true },
        { descripcion: 'ADICIONAL-Mov/DesmCentrífuga decant 2Rig', costoUnitario: 1884155.84, unidadCobro: 'serv', codigoMarbar: '006001000000271', item: '62', activo: true },
        { descripcion: 'ADICIONAL-DTM Centrífuga decantad 2Rig', costoUnitario: 1583526.46, unidadCobro: 'serv', codigoMarbar: '006001000000296', item: '63', activo: true },
        { descripcion: 'ADICIONAL-Zaranda secadora 2Rig', costoUnitario: 84583.55, unidadCobro: 'día', codigoMarbar: '006001000000085', item: '64', activo: true },
        { descripcion: 'ADICIONAL-Zaranda secadora Stand by 2Rig', costoUnitario: 59208.48, unidadCobro: 'día', codigoMarbar: '006001000000064', item: '65', activo: true },
        { descripcion: 'ADICIONAL-Mov/Desm Zaranda secadora 2Rig', costoUnitario: 1884155.84, unidadCobro: 'serv', codigoMarbar: '006001000000317', item: '66', activo: true },
        { descripcion: 'ADICIONAL-DTM Zaranda secadora 2Rig', costoUnitario: 1583526.46, unidadCobro: 'serv', codigoMarbar: '006001000000318', item: '67', activo: true },
        { descripcion: 'ADICIONAL-Tanque CaCl2 Stand By 2Rig', costoUnitario: 96337.5, unidadCobro: 'día', codigoMarbar: '006001000000509', item: '68', activo: true },
        { descripcion: 'ADICIONAL-Unidad de Dewatering 2Rig', costoUnitario: 84583.55, unidadCobro: 'día', codigoMarbar: '006001000000086', item: '69', activo: true },
        { descripcion: 'ADICIONAL-Unidad Dewatering Stnd By 2Rig', costoUnitario: 59208.48, unidadCobro: 'día', codigoMarbar: '006001000000087', item: '70', activo: true }
      ];

      try {
        for (const template of servicioTemplates) {
          await operations.handleServicioTipoSubmit(template);
        }
        alert('✅ Catálogo completo de servicios Excel cargado exitosamente (70 servicios con códigos MARBAR)');
      } catch (error) {
        console.error('Error cargando servicios:', error);
        alert('❌ Error cargando servicios. Ver consola para detalles.');
      }
    }
  };

  // Populate empresa dropdown options for form fields
  const servicioFormFieldsWithEmpresas = servicioTipoFormFields.map(field => {
    if (field.key === 'empresaCodigo') {
      return {
        ...field,
        options: [
          { value: '', label: '-- Sin asignar --' },
          ...empresaOptions
        ]
      };
    }
    return field;
  });

  return (
    <>
      {/* Empresa Filter */}
      <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
        <h3 style={{ color: '#333', marginBottom: '15px' }}>🏢 Filtrar Servicios por Empresa</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <label htmlFor="empresa-filter" style={{ fontWeight: 'bold', color: '#666' }}>Empresa:</label>
          <select
            id="empresa-filter"
            value={empresaFilter}
            onChange={(e) => setEmpresaFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '300px',
              cursor: 'pointer'
            }}
          >
            <option value="">📋 Todas las empresas</option>
            {data.empresas.map(empresa => (
              <option key={empresa.id} value={empresa.codigo}>
                {empresa.nombre} {empresa.numeroContrato ? `(Contrato: ${empresa.numeroContrato})` : ''}
              </option>
            ))}
          </select>
          {empresaFilter && (
            <button
              onClick={() => setEmpresaFilter('')}
              style={{
                padding: '8px 15px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: '#ff9800',
                color: 'white',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Limpiar Filtro
            </button>
          )}
          {empresaFilter && (
            <span style={{ color: '#666', fontSize: '14px' }}>
              Mostrando: {sortedServicios.length} servicio(s)
            </span>
          )}
        </div>
      </div>

      {/* Servicios Section */}
      <StandardSection
        config={createEntitySectionConfig(
          'Tipos de Servicios',
          '📦',
          sortedServicios,
          servicioFormFieldsWithEmpresas,
          servicioTipoColumns,
          operations.handleServicioTipoSubmit,
          async () => { alert('Esta entidad no tiene un estado de activación/desactivación.') },
          operations.handleServicioTipoDelete,
          {
            description: 'Define los tipos de servicios disponibles con códigos MARBAR para integración ServiceMax',
            quickSetup: {
              title: 'Catálogo Completo Excel',
              description: 'Carga 70 servicios del análisis LP-Servicios con códigos MARBAR auténticos y costos reales del Excel',
              buttonLabel: '📊 Cargar Catálogo Excel Completo',
              onQuickSetup: handleServiciosQuickSetup
            },
            sortConfig: sortConfig,
            onSort: handleSort,
          }
        )}
      />

      {/* Summary Section */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#f3e5f5',
        borderRadius: '12px',
        border: '2px solid #9C27B0'
      }}>
        <h3 style={{ color: '#7B1FA2', marginBottom: '15px' }}>📊 Resumen de Servicios</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginTop: '15px'
        }}>
          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🔧 Total Servicios
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#9C27B0'
            }}>
              {data.serviciosTipo.length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              ✅ Servicios Activos
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#4CAF50'
            }}>
              {data.serviciosTipo.filter(servicio => servicio.activo).length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              💰 Tarifa Más Alta
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#FF9800'
            }}>
              ${data.serviciosTipo.length > 0
                ? Math.max(...data.serviciosTipo.map(s => s.costoUnitario || 0))
                : 0
              }/h
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              💰 Tarifa Promedio
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#2196F3'
            }}>
              ${data.serviciosTipo.length > 0
                ? Math.round(data.serviciosTipo.reduce((sum, servicio) => sum + (servicio.costoUnitario || 0), 0) / data.serviciosTipo.length)
                : 0
              }/h
            </div>
          </div>
        </div>

        {/* Lista de Servicios */}
        <div style={{ marginTop: '20px' }}>
          <h4 style={{ color: '#7B1FA2', marginBottom: '10px' }}>Lista de Servicios (ordenados por código MARBAR):</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px' }}>
            {data.serviciosTipo
              .sort((a, b) => {
                // Sort by MARBAR code if both have codes, otherwise by description
                if (a.codigoMarbar && b.codigoMarbar) {
                  return a.codigoMarbar.localeCompare(b.codigoMarbar);
                }
                if (a.codigoMarbar && !b.codigoMarbar) return -1;
                if (!a.codigoMarbar && b.codigoMarbar) return 1;
                return a.descripcion.localeCompare(b.descripcion);
              })
              .map(servicio => (
              <div key={servicio.id} style={{
                padding: '15px',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #ddd'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#7B1FA2' }}>
                  🔧 {servicio.descripcion}
                </div>
                {servicio.codigoMarbar && (
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    Código MARBAR: {servicio.codigoMarbar}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#666', fontSize: '14px' }}>
                    ${servicio.costoUnitario} / {servicio.unidadCobro}
                  </span>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    backgroundColor: servicio.activo ? '#e8f5e8' : '#ffebee',
                    color: servicio.activo ? '#2e7d32' : '#c62828'
                  }}>
                    {servicio.activo ? 'ACTIVO' : 'INACTIVO'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Servicios con Mayor Tarifa */}
        {data.serviciosTipo.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ color: '#7B1FA2', marginBottom: '10px' }}>Servicios de Mayor Tarifa:</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '10px' }}>
              {data.serviciosTipo
                .sort((a, b) => (b.costoUnitario || 0) - (a.costoUnitario || 0))
                .slice(0, 6)
                .map(servicio => (
                  <div key={servicio.id} style={{
                    padding: '15px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #ddd'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#7B1FA2' }}>
                      🔧 {servicio.descripcion}
                    </div>
                    <div style={{ fontSize: '18px', color: '#9C27B0', fontWeight: 'bold' }}>
                      ${servicio.costoUnitario} / {servicio.unidadCobro}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Unidad de cobro: {servicio.unidadCobro}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {data.serviciosTipo.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            No hay servicios registrados. Usa la carga rápida para empezar con servicios estándar.
          </div>
        )}
      </div>
    </>
  );
};